import React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    FooterCopyrightText,
    LinkWrapper,
    MobileAppVersion,
    EmptyLinkWrapper,
    FooterCopyrightWrapper,
} from './FooterCopyright.style';
import { FooterCopyrightState } from 'src/domains/layouts/webview/modules/layoutSkeleton/footer/copyright/FooterCopyright.state';
import { LanguageTokenType } from 'src/domains/layouts/state/languagesState/LanguagesUtils';
import { useCommon } from 'src/domains/common/Common';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

const TermsAndConditions = observer('TermsAndConditions', () => {
    const common = useCommon();
    const languagesState = LanguagesState.get(common);
    const configComponents = ConfigComponents.get(common);

    if (configComponents.config.hasFooterCopyrightTCLink) {
        return languagesState.translateTokens(
            languagesState.getTranslation(
                'footer.copyright.general-terms-and-conditions',
                'In order to register for this website, the user is required to accept the [generalTermsAndConditions]. In the event the General Terms and Conditions are updated, existing users may choose to discontinue using the products and services before the said update shall become effective, which is a minimum of two weeks after it has been announced.'
            ),

            (singleParam: LanguageTokenType) => {
                if (singleParam.tag === 'generalTermsAndConditions') {
                    return (
                        <LinkWrapper
                            to={{ account: 'static', static: 'terms-and-conditions' }}
                            dataTest='terms-and-conditions'
                        >
                            General Terms and Conditions
                        </LinkWrapper>
                    );
                }
            }
        );
    } else {
        return null;
    }
});
const CookiePolicy = observer('CookiePolicy', () => {
    const common = useCommon();
    const languagesState = LanguagesState.get(common);

    return languagesState.translateTokens(
        languagesState.getTranslation(
            'footer.copyright.cookie-policy',
            'This site uses cookies, for more information please see our [cookiePolicy].'
        ),

        (singleParam: LanguageTokenType) => {
            if (singleParam.tag === 'cookiePolicy') {
                return (
                    <LinkWrapper
                        to={{ account: 'static', static: 'cookie-policy' }}
                        dataTest='cookie-policy'
                    >
                        cookie policy
                    </LinkWrapper>
                );
            }
        }
    );
});

export const FooterCopyrightDefault = observer(
    'FooterCopyrightDefault',
    ({ alreadyClosed }: { alreadyClosed?: boolean }) => {
        const common = useCommon();
        const state = FooterCopyrightState.get(common);

        const renderCopyRight = (): React.ReactNode => {
            if (typeof DOMParser === 'undefined') {
                return null;
            }

            const doc = new DOMParser().parseFromString(state.copyrightDescription, 'text/html');

            const renderLink = (url: string | null, content: string | React.ReactNode[], dataTest?: string) =>
                url ? (
                    <LinkWrapper
                        dataTest={dataTest}
                        to={url}
                    >
                        {content}
                    </LinkWrapper>
                ) : (
                    <EmptyLinkWrapper data-test={dataTest}>{content}</EmptyLinkWrapper>
                );

            const renderNode = (node: ChildNode, index: number): React.ReactNode => {
                if (node.nodeType === Node.TEXT_NODE) {
                    const text = node.textContent ?? '';

                    if (!text.includes('{{licenceNumber}}')) {
                        return text;
                    }

                    const parts = text.split('{{licenceNumber}}');

                    return parts.map((part, partIndex) => (
                        <React.Fragment key={`${index}-${partIndex}`}>
                            {part}

                            {partIndex < parts.length - 1 &&
                                renderLink(state.licenceUrl, state.licenceNumber, 'licence-number')}
                        </React.Fragment>
                    ));
                }

                if (node.nodeType !== Node.ELEMENT_NODE) {
                    return null;
                }

                const element = node as HTMLElement;
                const children = Array.from(element.childNodes).map(renderNode);

                if (element.tagName.toLowerCase() === 'a') {
                    return renderLink(element.getAttribute('href'), children);
                }

                return React.createElement(element.tagName.toLowerCase(), { key: index }, children);
            };

            return Array.from(doc.body.childNodes).map(renderNode);
        };

        return (
            <>
                <TermsAndConditions />
                &nbsp;{renderCopyRight()}&nbsp;
                {alreadyClosed ? null : (
                    <>
                        <CookiePolicy />
                    </>
                )}
            </>
        );
    }
);

export const FooterCopyright = observer('FooterCopyright', ({ alreadyClosed }: { alreadyClosed?: boolean }) => {
    const common = useCommon();
    const env = EnvironmentState.get(common);
    const { mobileWrapperType, mobileWrapperVersion } = env.envVariables;

    const version = mobileWrapperVersion ?? null;

    return (
        <>
            <FooterCopyrightWrapper alreadyClosed={alreadyClosed}>
                <FooterCopyrightText
                    alreadyClosed={alreadyClosed}
                    data-test='FooterCopyright'
                >
                    <FooterCopyrightDefault alreadyClosed={alreadyClosed} />
                </FooterCopyrightText>
            </FooterCopyrightWrapper>

            {mobileWrapperType === null || version === null ? null : (
                <MobileAppVersion>Mobile app version: {mobileWrapperVersion}</MobileAppVersion>
            )}
        </>
    );
});
